/**
 * ContentPageEditor — editor for content sub-pages (Angebot, Methoden, UeberMich)
 *
 * Converted from legacy React.createElement() calls to proper JSX.
 */
import { EditorField } from '@/components/editor/EditorField';
import { EditorTextarea } from '@/components/editor/EditorTextarea';
import { EditorList } from '@/components/editor/EditorList';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type PageType = 'angebot' | 'methoden' | 'ueberMich';

interface ContentPageEditorProps {
  page: PageType;
  settings: any;
  onUpdate: (settings: any) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ContentPageEditor({ page, settings, onUpdate }: ContentPageEditorProps) {
  const pageKey = page === 'angebot' ? 'pageAngebot' : page === 'methoden' ? 'pageMethoden' : 'pageUeberMich';
  const data = settings[pageKey] || {};

  const updateField = (field: string, value: any) => {
    onUpdate({ ...settings, [pageKey]: { ...data, [field]: value } });
  };

  const updateListItem = (listKey: string, items: any[]) => {
    onUpdate({ ...settings, [pageKey]: { ...data, [listKey]: items } });
  };

  if (page === 'angebot') {
    return (
      <div className="editor-panel">
        <h3 className="editor-title" style={{ marginBottom: 12 }}>
          {'\u{1F4B0}'} Angebot-Seite bearbeiten
        </h3>
        <EditorField
          label="Seitentitel"
          value={data.heroTitle || ''}
          onChange={(v) => updateField('heroTitle', v)}
          placeholder="Unsere Leistungen"
        />
        <EditorTextarea
          label="Untertitel"
          value={data.heroSubtitle || ''}
          onChange={(v) => updateField('heroSubtitle', v)}
          placeholder="Kurze Beschreibung"
          rows={2}
        />
        <EditorList
          label="Pakete"
          items={(data.packages || []).map((p: any) => ({
            name: p.name || '',
            price: p.price || '',
            description: p.description || '',
            features: p.features || '',
            highlighted: !!p.highlighted,
          }))}
          onChange={(items) => updateListItem('packages', items)}
          fields={[
            { key: 'name', placeholder: 'Paketname' },
            { key: 'price', placeholder: 'ab CHF 150/Mt.' },
            { key: 'description', placeholder: 'Kurzbeschreibung' },
            { key: 'features', placeholder: 'Features (kommagetrennt)' },
            { key: 'highlighted', placeholder: 'Hervorgehoben', type: 'checkbox' },
          ]}
          maxItems={5}
        />
        <EditorField
          label="CTA Titel"
          value={data.ctaTitle || ''}
          onChange={(v) => updateField('ctaTitle', v)}
          placeholder="Bereit loszulegen?"
        />
        <EditorTextarea
          label="CTA Text"
          value={data.ctaText || ''}
          onChange={(v) => updateField('ctaText', v)}
          placeholder="Kontaktieren Sie uns..."
          rows={2}
        />
        <EditorField
          label="CTA Button"
          value={data.ctaButtonText || ''}
          onChange={(v) => updateField('ctaButtonText', v)}
          placeholder="Jetzt anfragen"
        />
      </div>
    );
  }

  if (page === 'methoden') {
    return (
      <div className="editor-panel">
        <h3 className="editor-title" style={{ marginBottom: 12 }}>
          {'\u{1F527}'} Methoden-Seite bearbeiten
        </h3>
        <EditorField
          label="Seitentitel"
          value={data.heroTitle || ''}
          onChange={(v) => updateField('heroTitle', v)}
          placeholder="Unser Vorgehen"
        />
        <EditorTextarea
          label="Untertitel"
          value={data.heroSubtitle || ''}
          onChange={(v) => updateField('heroSubtitle', v)}
          placeholder="Kurze Beschreibung"
          rows={2}
        />
        <EditorTextarea
          label="Einleitungstext"
          value={data.introText || ''}
          onChange={(v) => updateField('introText', v)}
          placeholder="Beschreibung Ihrer Arbeitsweise..."
          rows={3}
        />
        <EditorList
          label="Schritte"
          items={(data.steps || []).map((s: any) => ({
            title: s.title || '',
            description: s.description || '',
          }))}
          onChange={(items) => updateListItem('steps', items)}
          fields={[
            { key: 'title', placeholder: 'Schritt-Titel' },
            { key: 'description', placeholder: 'Beschreibung' },
          ]}
          maxItems={6}
        />
        <EditorList
          label="Tools & Werkzeuge"
          items={(data.tools || []).map((t: any) => ({
            name: t.name || '',
            description: t.description || '',
          }))}
          onChange={(items) => updateListItem('tools', items)}
          fields={[
            { key: 'name', placeholder: 'Tool-Name' },
            { key: 'description', placeholder: 'Beschreibung' },
          ]}
          maxItems={6}
        />
        <EditorField
          label="Philosophie-Titel"
          value={data.philosophyTitle || ''}
          onChange={(v) => updateField('philosophyTitle', v)}
          placeholder="Unsere Philosophie"
        />
        <EditorTextarea
          label="Philosophie-Text"
          value={data.philosophyText || ''}
          onChange={(v) => updateField('philosophyText', v)}
          placeholder="Beschreibung Ihrer Philosophie..."
          rows={3}
        />
      </div>
    );
  }

  // ueberMich
  return (
    <div className="editor-panel">
      <h3 className="editor-title" style={{ marginBottom: 12 }}>
        {'\u{1F464}'} \u00dcber-mich-Seite bearbeiten
      </h3>
      <EditorField
        label="Seitentitel"
        value={data.heroTitle || ''}
        onChange={(v) => updateField('heroTitle', v)}
        placeholder="\u00dcber uns"
      />
      <EditorTextarea
        label="Untertitel"
        value={data.heroSubtitle || ''}
        onChange={(v) => updateField('heroSubtitle', v)}
        placeholder="Kurze Beschreibung"
        rows={2}
      />
      <EditorTextarea
        label="Einleitungstext"
        value={data.introText || ''}
        onChange={(v) => updateField('introText', v)}
        placeholder="Wer sind wir..."
        rows={3}
      />
      <EditorField
        label="Bild-URL"
        value={data.imageUrl || ''}
        onChange={(v) => updateField('imageUrl', v)}
        placeholder="https://..."
      />
      <EditorList
        label="Werdegang"
        items={(data.timeline || []).map((t: any) => ({
          year: t.year || '',
          text: t.text || '',
        }))}
        onChange={(items) => updateListItem('timeline', items)}
        fields={[
          { key: 'year', placeholder: 'Jahr' },
          { key: 'text', placeholder: 'Beschreibung' },
        ]}
        maxItems={8}
      />
      <EditorList
        label="Werte"
        items={(data.values || []).map((v: any) => ({
          title: v.title || '',
          description: v.description || '',
        }))}
        onChange={(items) => updateListItem('values', items)}
        fields={[
          { key: 'title', placeholder: 'Wert' },
          { key: 'description', placeholder: 'Beschreibung' },
        ]}
        maxItems={6}
      />
      <EditorField
        label="Zertifizierungen"
        value={data.certifications || ''}
        onChange={(v) => updateField('certifications', v)}
        placeholder="Zertifikate, Mitgliedschaften..."
      />
    </div>
  );
}
